import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { X, Download, Monitor, Share, Plus } from "lucide-react";

export function AdminInstallPrompt({
  storageKey = "adminInstallSnoozedUntil",
  title = "Install Admin App",
  description = "Install the Admin Dashboard for quick access to bookings, drivers, and stats.",
  headerBg = "bg-gradient-to-r from-gray-800 to-gray-900",
}) {
  const [showPrompt, setShowPrompt] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    // Already installed?
    const wasInstalled = window.matchMedia('(display-mode: standalone)').matches
      || window.navigator.standalone === true;
    setIsStandalone(wasInstalled);
    if (wasInstalled) return;

    // "Remind me later" snooze: only hides for 24 hours, then nag again
    const snoozedUntil = parseInt(localStorage.getItem(storageKey) || "0", 10);
    const isSnoozed = snoozedUntil && Date.now() < snoozedUntil;

    // Detect iOS (PWA install on iOS requires Share → Add to Home Screen)
    const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(isIOSDevice);

    // Capture native install event (Android / Desktop Chrome)
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      if (!isSnoozed) setShowPrompt(true);
    };
    window.addEventListener('beforeinstallprompt', handleBeforeInstall);

    // Show on every page load until installed (after a small delay so it doesn't flash)
    if (!isSnoozed) {
      const t = setTimeout(() => setShowPrompt(true), 1500);
      return () => {
        window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
        clearTimeout(t);
      };
    }

    return () => window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
  }, [storageKey]);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setShowPrompt(false);
        // Permanent dismiss only after they actually accept install
        localStorage.setItem(storageKey, String(Date.now() + 365 * 24 * 60 * 60 * 1000));
      }
      setDeferredPrompt(null);
    } else if (!isIOS) {
      alert("To install: tap your browser menu (⋮) → 'Install app' or 'Add to Home Screen'.\n\nIf you don't see that option, try Chrome, Edge, or Brave on Android, or Chrome/Edge on Desktop.");
    }
  };

  const handleSnooze = () => {
    setShowPrompt(false);
    // Remind again in 24 hours
    localStorage.setItem(storageKey, String(Date.now() + 24 * 60 * 60 * 1000));
  };

  if (isStandalone || !showPrompt) {
    return null;
  }

  return (
    <div className="fixed top-4 right-4 z-50 animate-in slide-in-from-top duration-300" data-testid="admin-install-prompt">
      <div className="bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden w-80">
        {/* Header */}
        <div className={`${headerBg} px-4 py-3 flex items-center justify-between`}>
          <div className="flex items-center gap-2">
            <Monitor className="w-5 h-5 text-white" />
            <span className="text-white font-semibold text-sm">{title}</span>
          </div>
          <button 
            onClick={handleSnooze}
            className="text-white/80 hover:text-white p-1 rounded-full hover:bg-white/20 transition-colors"
            data-testid="dismiss-admin-install"
            title="Remind me later (24h)"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4">
          <p className="text-sm text-gray-600 mb-4">
            {description}
          </p>

          {isIOS ? (
            // iOS Instructions
            <div className="bg-gray-50 rounded-lg p-3 mb-3">
              <p className="text-xs font-medium text-gray-700 mb-2">To install on iOS:</p>
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Share className="w-4 h-4 text-blue-600" />
                  <span>Tap <strong>Share</strong> button</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Plus className="w-4 h-4 text-green-600" />
                  <span>Select <strong>"Add to Home Screen"</strong></span>
                </div>
              </div>
            </div>
          ) : (
            // Desktop/Android Install Button
            <Button 
              onClick={handleInstall}
              className="w-full bg-gray-800 hover:bg-gray-900 text-white font-semibold py-2.5 rounded-lg flex items-center justify-center gap-2"
              data-testid="install-app-btn"
            >
              <Download className="w-4 h-4" />
              {title}
            </Button>
          )}

          <p className="text-xs text-center text-gray-400 mt-3">
            Works offline • Fast access
          </p>
        </div>
      </div>
    </div>
  );
}
